import { useEffect, useRef, useState } from "react";
import type { CloseBillTipIn } from "../../../../../api/bills";
import type { TipSettings } from "../../../../../api/tipSettings";
import {
  formatCents,
  parseMoneyToCents,
  sanitizeMoneyInput,
} from "../../../../../utils/money";
import {
  buildTipRequest,
  needsTipConfirmation,
  tipCentsOf,
  validateTip,
  type TipIssue,
} from "./tipMath";
import { keypadTextOf, type MoneyKeypadKey } from "./keypadState";

interface UseTipEntryArgs {
  /** Модалът е отворен. */
  open: boolean;
  /** Секцията е отворена за въвеждане (модалът е отворен, бакшишите са
   *  включени, не е повторен опит, касата офлайн ги умее). */
  active: boolean;
  /** Поръчаното след отстъпки (живо — `bill.total_amount` от кеша). */
  orderedAmount: string | number;
  settings: TipSettings | null;
  /** Методът на бакшиша — винаги методът на сметката (`null` — без бакшиш). */
  tipMethodId: number | null;
  /** Защо методът на сметката не може да носи бакшиш (пътят към
   *  фискалното устройство) — с кода на BE; `null` — може. */
  methodRefusal: TipIssue | null;
  /** Въведен бакшиш е отпаднал, защото секцията се е затворила (без
   *  интернет към каса без бакшиш, изключени настройки) — да се каже. */
  onTipDropped?: () => void;
}

export interface OrderedAmountChange {
  fromCents: number;
  toCents: number;
}

/**
 * Състоянието на картата „Бакшиш" (`TipCard`) — общо за „Закрий" и
 * „Раздели и плати". Правилата са в `tipMath.ts`; тук е само кой какво е
 * въвел и кога то престава да важи:
 *
 * - „Общо с бакшиш" тръгва от поръчаното (бакшиш 0) и го следва, докато не
 *   бъде пипнато;
 * - пипнато ли е, поръчаното в момента на пипането е „прегледаното": смени
 *   ли се после (друг оператор), секцията показва старо → ново, потвърждението
 *   пада и закриването чака нов преглед (бутонът „Прегледано" или нова сума);
 * - методът на бакшиша е методът на сметката — отделен избор няма;
 * - потвърждението над прага важи само за ТОЧНО потвърдената сума.
 */
export function useTipEntry({
  open,
  active,
  orderedAmount,
  settings,
  tipMethodId: billTipMethodId,
  methodRefusal,
  onTipDropped,
}: UseTipEntryArgs) {
  const orderedCents = parseMoneyToCents(orderedAmount) ?? 0;

  // null = непипнато (следва поръчаното); иначе каквото е написано.
  const [totalText, setTotalText] = useState<string | null>(null);
  const [reviewedOrderedCents, setReviewedOrderedCents] = useState<number | null>(null);
  const [confirmedTipCents, setConfirmedTipCents] = useState<number | null>(null);
  // Картата е скрита зад „Добави бакшиш", докато човекът не я отвори.
  const [isOpened, setIsOpened] = useState(false);

  const isTouched = active && totalText !== null;
  const enteredCents = isTouched ? parseMoneyToCents(totalText) : null;
  const tipCents = active ? tipCentsOf(orderedCents, enteredCents) : 0;

  const tipMethodId = active ? billTipMethodId : null;
  const methodIssue = active ? methodRefusal : null;

  // Затворена секция → всичко отначало. Въведен бакшиш, отпаднал при ОТВОРЕН
  // модал, се казва на човека (иначе би закрил без него, без да разбере);
  // при затваряне на самия модал — не.
  const lastActiveTipCentsRef = useRef(0);
  if (active) lastActiveTipCentsRef.current = tipCents;
  const onTipDroppedRef = useRef(onTipDropped);
  onTipDroppedRef.current = onTipDropped;
  useEffect(() => {
    if (active) return;
    if (open && lastActiveTipCentsRef.current > 0) onTipDroppedRef.current?.();
    lastActiveTipCentsRef.current = 0;
    setTotalText(null);
    setReviewedOrderedCents(null);
    setConfirmedTipCents(null);
    setIsOpened(false);
  }, [active, open]);

  const orderedChange: OrderedAmountChange | null =
    isTouched &&
    reviewedOrderedCents !== null &&
    reviewedOrderedCents !== orderedCents
      ? { fromCents: reviewedOrderedCents, toCents: orderedCents }
      : null;

  const needsConfirmation =
    active && settings !== null && tipCents > 0 && needsTipConfirmation(tipCents, settings);
  const isConfirmed =
    needsConfirmation && orderedChange === null && confirmedTipCents === tipCents;

  const issue: TipIssue | null =
    active && settings !== null
      ? validateTip({
          tipCents,
          settings,
          tipMethodId,
          allowedMethodIds: tipMethodId !== null && methodIssue === null ? [tipMethodId] : [],
          confirmedTipCents: isConfirmed ? confirmedTipCents : null,
          methodRefusal: methodIssue,
        })
      : null;

  /** Писане в „Сума дадена от клиента" — и нов преглед на поръчаното. Всяка промяна
   *  на сумата сваля потвърждението над прага: 50 → 60 → 50 иска ново
   *  потвърждение, старото не „оживява" (BE връзва потвърждението за сумата,
   *  тук — и за момента, в който е дадено). */
  const changeTotalWithTip = (raw: string) => {
    const nextText = sanitizeMoneyInput(raw);
    if (nextText !== totalText) setConfirmedTipCents(null);
    setTotalText(nextText);
    setReviewedOrderedCents(orderedCents);
  };

  /** Екранната клавиатура (`MoneyKeypad`): като писане в полето — от
   *  предишния текст (функционално), за да не се губи цифра при бързо тракане. */
  const pressKeypadKey = (key: MoneyKeypadKey) => {
    setTotalText((previousText) => keypadTextOf(previousText ?? "", key));
    setConfirmedTipCents(null);
    setReviewedOrderedCents(orderedCents);
  };

  /** „Добави бакшиш" — показва картата (бакшишът още е 0). */
  const openTip = () => setIsOpened(true);

  /** „Премахни" — бакшишът е 0 и картата пак се скрива зад бутона. */
  const removeTip = () => {
    setTotalText(null);
    setReviewedOrderedCents(null);
    setConfirmedTipCents(null);
    setIsOpened(false);
  };

  /** Излизане от полето / Enter: „100" → „100.00"; празно → пак поръчаното. */
  const normalizeTotalWithTip = () => {
    if (totalText === null) return;
    const cents = parseMoneyToCents(totalText);
    if (cents === null) {
      setTotalText(null);
      return;
    }
    setTotalText(formatCents(cents));
  };

  const setConfirmed = (checked: boolean) =>
    setConfirmedTipCents(checked ? tipCents : null);

  /** „Прегледано" — новото поръчано е прието; „Сума дадена от клиента"
   *  остава, каквото е казано. */
  const acknowledgeOrderedChange = () => {
    setReviewedOrderedCents(orderedCents);
    setConfirmedTipCents(null);
  };

  /** Свежото поръчано (при „Закриване") различно ли е от прегледаното.
   *  Важи и при бакшиш, станал 0: „Общо с бакшиш" 100 при поръчано, качено
   *  от 92 на 100, иначе би закрило ТИХО без замисления бакшиш. */
  const isStaleAgainst = (freshOrderedCents: number) =>
    isTouched &&
    reviewedOrderedCents !== null &&
    reviewedOrderedCents !== freshOrderedCents;

  const buildRequest = (options?: {
    includeTotalWithTip?: boolean;
  }): CloseBillTipIn | undefined =>
    active && settings !== null
      ? buildTipRequest({
          tipCents,
          tipMethodId,
          reviewedOrderedCents: reviewedOrderedCents ?? orderedCents,
          settings,
          includeTotalWithTip: options?.includeTotalWithTip,
        })
      : undefined;

  return {
    active,
    orderedCents,
    /** Написаното в „Сума дадена от клиента" („" — нищо, полето показва поръчаното като подсказка). */
    enteredText: active ? (totalText ?? "") : "",
    isTouched,
    tipCents,
    tipMethodId,
    /** Методът на сметката не може да носи бакшиш — текстът под сумата. */
    methodIssue,
    needsConfirmation,
    isConfirmed,
    orderedChange,
    issue,
    /** Нещо пречи на закриването (грешка, неприет нов преглед). */
    blocksClose: issue !== null || orderedChange !== null,
    reviewedOrderedCents,
    /** Картата е отворена (или вече има въведено). */
    isOpened: active && (isOpened || isTouched),
    changeTotalWithTip,
    normalizeTotalWithTip,
    pressKeypadKey,
    openTip,
    removeTip,
    setConfirmed,
    acknowledgeOrderedChange,
    isStaleAgainst,
    buildRequest,
  };
}

export type TipEntryState = ReturnType<typeof useTipEntry>;
